import { useState } from "react";
import { UploadOutlined } from "@ant-design/icons";
import { Button, Modal, Progress, Space, Tag, Typography, message } from "antd";

import { ExerciseRecordingStatus } from "@fitness-app/data-models/entities/ExerciseRecording";
import { getErrorMessage } from "@fitness-app/utils";

import { UploadFileStatus, type StorageConfig, type UploadValidatorConfig } from "~/components/Upload/types";
import UploadInput from "~/components/Upload/UploadInput";
import { UploadFilesProvider, useUploadFilesContext } from "~/components/Upload/UploadProvider";
import { setRecordingUploaded, type SimulatorRecording } from "../writes/workoutWrites";

const MAX_VIDEO_SIZE_IN_BYTES = 100 * 1024 * 1024;

const RECORDING_STATUS_LABEL: Record<ExerciseRecordingStatus, string> = {
  [ExerciseRecordingStatus.PENDING]: "oczekuje",
  [ExerciseRecordingStatus.SCHEDULED]: "zaplanowane",
  [ExerciseRecordingStatus.RECORDED]: "nagrane",
  [ExerciseRecordingStatus.RATED]: "ocenione",
  [ExerciseRecordingStatus.ARCHIVED]: "zarchiwizowane",
};

// Ten sam bucket i katalog co w zakładce Pomiary — nagranie z symulatora ma leżeć tam,
// gdzie reszta filmów klientki, a nie w osobnym miejscu.
const getUploadConfig = (
  traineeId: string,
): { validatorConfig: UploadValidatorConfig; storageConfig: StorageConfig } => ({
  validatorConfig: {
    maxFilesListSize: MAX_VIDEO_SIZE_IN_BYTES,
    maxFilesListLength: 1,
    maxFileSize: MAX_VIDEO_SIZE_IN_BYTES,
    allowedMimeTypes: ["video/mp4", "video/quicktime", "video/x-m4v", "video/webm"],
  },
  storageConfig: {
    isPublicBucket: false,
    bucketName: "users",
    objectPath: `${traineeId}/videos`,
  },
});

type Props = {
  recording: SimulatorRecording;
  traineeId: string;
  date: string;
  createdBy: string;
  onDone: () => void;
};

const UploadContent = ({
  recording,
  traineeId,
  date,
  createdBy,
  onClose,
  onDone,
}: Props & { onClose: () => void }) => {
  const { files, removeFile, resetFilesList } = useUploadFilesContext();
  const [isSaving, setIsSaving] = useState(false);

  const uploadedFile = files.find((file) => file.status === UploadFileStatus.SUCCESS);
  const uploadingFile = files.find((file) => file.status === UploadFileStatus.UPLOADING);

  const save = async () => {
    if (!uploadedFile?.url) {
      return;
    }
    setIsSaving(true);
    try {
      await setRecordingUploaded({
        recording,
        traineeId,
        date,
        createdBy,
        uploaded: true,
        mode: "single",
        url: uploadedFile.url,
        path: uploadedFile.objectPath,
      });
      resetFilesList();
      onDone();
      onClose();
    } catch (error) {
      void message.error(`Nie udało się zapisać filmu: ${getErrorMessage(error)}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Space direction="vertical" size={12} className="w-full">
      {!uploadedFile && !uploadingFile ? (
        <UploadInput multiple={false}>
          <div className="flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 p-8">
            <UploadOutlined className="mb-2 text-4xl text-gray-400" />
            <Typography.Text className="text-gray-600">Wybierz plik wideo</Typography.Text>
            <Typography.Text type="secondary" className="mt-1 text-xs">
              MP4, MOV, M4V, WebM (max 100MB)
            </Typography.Text>
          </div>
        </UploadInput>
      ) : null}

      {uploadingFile ? (
        <div className="rounded-lg border border-gray-200 p-4">
          <Typography.Text>{uploadingFile.originalName}</Typography.Text>
          <Progress percent={uploadingFile.progress || 0} status="active" />
        </div>
      ) : null}

      {uploadedFile ? (
        <div className="flex items-center justify-between gap-2 rounded-lg border border-green-200 bg-green-50 p-4">
          <Typography.Text className="text-green-700">{uploadedFile.originalName}</Typography.Text>
          <Button size="small" danger onClick={() => removeFile(uploadedFile)}>
            Usuń
          </Button>
        </div>
      ) : null}

      <div className="flex justify-end gap-2">
        <Button onClick={onClose}>Anuluj</Button>
        <Button type="primary" disabled={!uploadedFile} loading={isSaving} onClick={() => void save()}>
          Zapisz nagranie
        </Button>
      </div>
    </Space>
  );
};

/**
 * Sterowanie nagraniem przy konkretnym ćwiczeniu: podstawienie statusu („mock") albo wgranie
 * prawdziwego pliku. Wiersz nagrania zakłada workouts-service — obie ścieżki tylko przestawiają
 * jego status, tak jak robi to klientka wgrywająca film.
 */
const SimulatorExerciseRecordingControl = ({ recording, traineeId, date, createdBy, onDone }: Props) => {
  const [uploadOpen, setUploadOpen] = useState(false);
  const [isBusy, setIsBusy] = useState(false);

  const isUploaded =
    recording.status === ExerciseRecordingStatus.RECORDED || recording.status === ExerciseRecordingStatus.RATED;
  const isRated = recording.status === ExerciseRecordingStatus.RATED;
  // Cofać wolno wyłącznie nagranie podstawione przez symulator. Prawdziwe wideo — wgrane przez
  // klientkę albo przez trenerkę normalnym flow — zniknęłoby bez śladu w rejestrze, więc panel
  // czyszczenia nie miałby czego przywrócić.
  const isSimulated = recording.metadata?.simulator === true;

  const flip = async (uploaded: boolean) => {
    setIsBusy(true);
    try {
      await setRecordingUploaded({ recording, traineeId, date, createdBy, uploaded, mode: "single" });
      onDone();
    } catch (error) {
      void message.error(getErrorMessage(error));
    } finally {
      setIsBusy(false);
    }
  };

  const uploadConfig = getUploadConfig(traineeId);

  return (
    <Space size={8} wrap>
      <Tag color={isUploaded ? "green" : "default"}>
        nagranie: {RECORDING_STATUS_LABEL[recording.status] ?? recording.status}
      </Tag>

      {isRated ? (
        <Typography.Text type="secondary">ocenione — symulator tego nie rusza</Typography.Text>
      ) : isUploaded ? (
        isSimulated ? (
          <Button size="small" loading={isBusy} onClick={() => void flip(false)}>
            Cofnij nagranie
          </Button>
        ) : (
          <Typography.Text type="secondary">nagranie klientki — symulator go nie rusza</Typography.Text>
        )
      ) : (
        <>
          <Button size="small" loading={isBusy} onClick={() => void flip(true)}>
            Mock nagrania
          </Button>
          <Button size="small" type="primary" ghost onClick={() => setUploadOpen(true)}>
            Wgraj wideo
          </Button>
        </>
      )}

      <Modal
        open={uploadOpen}
        title={`Wgraj wideo: ${recording.exerciseName}`}
        footer={null}
        destroyOnClose
        width={520}
        onCancel={() => setUploadOpen(false)}
      >
        <UploadFilesProvider {...uploadConfig}>
          <UploadContent
            recording={recording}
            traineeId={traineeId}
            date={date}
            createdBy={createdBy}
            onDone={onDone}
            onClose={() => setUploadOpen(false)}
          />
        </UploadFilesProvider>
      </Modal>
    </Space>
  );
};

export default SimulatorExerciseRecordingControl;
